import React, { useState } from 'react';
import { FiZap } from 'react-icons/fi';
import { BetRecommendation } from '../services/tipsterService';
import { useTranslation } from '../contexts/TranslationContext';
import { translateEvent } from '../utils/translateEvent';
import { getTierLabel } from '../utils/tierLabel';

interface ExpertPickPanelProps {
  recommendation: BetRecommendation;
  resolved?: boolean;
  ongoing?: boolean;
}

const ExpertPickPanel: React.FC<ExpertPickPanelProps> = ({ recommendation, resolved = false, ongoing = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const getResultLabel = (result?: string) => {
    switch (result) {
      case 'WIN':
        return t.pickResults.won;
      case 'LOOSE':
        return t.pickResults.lost;
      case 'VOID':
        return t.pickResults.void;
      default:
        return null;
    }
  };

  const result = recommendation.bet_event?.result;
  const resultLabel = getResultLabel(result);
  const resultClass = result ? `expert-pick-row--${result.toLowerCase()}` : '';
  const ongoingClass = ongoing ? 'expert-pick-row--ongoing' : '';

  return (
    <div
      className={`expert-pick-row ${isExpanded ? 'expert-pick-row--expanded' : ''} ${resultClass} ${ongoingClass}`.trim()}
      onClick={toggleExpand}
    >
      <div className="expert-pick-row__main">
        <div className="expert-pick-row__teams">
          {recommendation.bet_event?.game ? (
            <>
              <span className="expert-pick-row__team">{translateEvent(recommendation.bet_event.game.home_team, t.eventsDictionary)}</span>
              <span className="expert-pick-row__vs">{t.common.vs}</span>
              <span className="expert-pick-row__team">{translateEvent(recommendation.bet_event.game.away_team, t.eventsDictionary)}</span>
            </>
          ) : (
            <span className="expert-pick-row__team">{t.common.gameUnavailable}</span>
          )}
        </div>
        <div className="expert-pick-row__details">
          {ongoing && (
            <span className="expert-pick-row__live-badge">
              <FiZap size={9} /> {t.common.live}
            </span>
          )}
          {resolved && resultLabel && (
            <span className={`expert-pick-row__result-badge expert-pick-row__result-badge--${result?.toLowerCase()}`}>
              {resultLabel}
            </span>
          )}
          {recommendation.tipster_tier && !resolved && (() => {
            const tier = recommendation.tipster_tier!;
            const isFree = tier.level === 0;
            const color = isFree ? null : (tier.color ?? '#58C9FA');
            const badgeStyle = color ? {
              background: `${color}22`,
              color,
            } : undefined;
            return (
              <span
                className={`expert-pick-row__tier-badge${isFree ? ' expert-pick-row__tier-badge--free' : ''}`}
                style={badgeStyle}
              >
                {getTierLabel(tier.level, tier.name, t)}
              </span>
            );
          })()}
          <span className="expert-pick-row__event">{translateEvent(recommendation.bet_event?.event ?? '', t.eventsDictionary)}</span>
          <span className="expert-pick-row__odds">
            {recommendation.bet_event?.odds != null ? recommendation.bet_event.odds.toFixed(2) : '—'}
          </span>
          {recommendation.stake ? (
            <span className="expert-pick-row__stake">{recommendation.stake}u</span>
          ) : null}
          {recommendation.bet_event?.game && (
            <span className="expert-pick-row__time">{formatDate(recommendation.bet_event.game.datetime)}</span>
          )}
        </div>
      </div>
      {recommendation.tipster_description && (
        <div className={`expert-pick-row__expanded ${isExpanded ? 'expert-pick-row__expanded--open' : ''}`}>
          <div className="expert-pick-row__description">
            {recommendation.tipster_description}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpertPickPanel;
